import { action, observable } from 'mobx';

import { SerializedData } from 'bernie-core';
import { AnalyticsStore, ContextStore, Store } from 'bernie-plugin-mobx';

import {
  SortPresentationType,
  FlightsFilterLoadedOptionType,
  LoadedSortPresentationType,
  FlightsFilterOptionType,
  FilterPresentationType
} from 'types/search/sort-filter';
import { FlightsFilterItemType } from 'types/shared';
import analyticsWrapper from 'utility/analytics-wrapper';
import { SearchCriteriaStore } from '.';
import { FlightsShoppingPwaFlightSearchResultsFlightsSearch, FlightsAnalytics } from '__generated__/api';
import AppliedFiltersSummary = FlightsShoppingPwaFlightSearchResultsFlightsSearch.AppliedFiltersSummary;
import AppliedFiltersType = FlightsShoppingPwaFlightSearchResultsFlightsSearch.AppliedFilters;
import ApplyAction = FlightsShoppingPwaFlightSearchResultsFlightsSearch.ApplyAction;
import ClearAllSelectionLabel = FlightsShoppingPwaFlightSearchResultsFlightsSearch.ClearAllSelectionLabel;
import ClearSelectionLabel = FlightsShoppingPwaFlightSearchResultsFlightsSearch.ClearSelectionLabel;
import DismissIcon = FlightsShoppingPwaFlightSearchResultsFlightsSearch.DismissIcon;
import RevealAction = FlightsShoppingPwaFlightSearchResultsFlightsSearch.RevealAction;
import TriggerFlightsSearchParams from 'types/shared/trigger-flights-search-params';

export interface SortAndFilterResult {
  appliedFiltersSummary?: AppliedFiltersSummary;
  applyAction: ApplyAction;
  clearAllSelectionLabel: ClearAllSelectionLabel;
  clearSelectionLabel: ClearSelectionLabel;
  dismiss: DismissIcon;
  dismissAnalytics: FlightsAnalytics.Fragment;
  filterPresentation?: FilterPresentationType;
  revealAction: RevealAction;
  revealActionAnalytics: FlightsAnalytics.Fragment;
  sortPresentation: SortPresentationType;
  title?: string;
  filterAnalyticsList?: FlightsAnalytics.Fragment[];
}

interface PreviousFilterSnapshotType {
  isFilterAction: boolean;
  previousAppliedSummarySnapshot: AppliedFiltersSummary;
}

export class SortFilterStore extends Store {
  @observable public sortAndFilterResult: SortAndFilterResult;
  @observable public loading: boolean;
  public actionInProgress = false;
  public initialFilterSkeletons: FlightsFilterOptionType[];
  @observable public showFiltersSkeleton: boolean;
  @observable public showFiltersUSF: boolean;

  public currentSelectedFilters: FlightsFilterItemType[] = [];

  public previousFilterSnapshot: PreviousFilterSnapshotType = {
    isFilterAction: false,
    previousAppliedSummarySnapshot: {
      __typename: 'FlightsAppliedFiltersSummary',
      appliedFilters: []
    }
  };

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
    this.actionInProgress = false;
    this.loading = false;
  }

  public hasSortApplied(): boolean {
    const sortPresentation = this.sortAndFilterResult?.sortPresentation as LoadedSortPresentationType;

    return (
      sortPresentation &&
      sortPresentation.selectedOption &&
      sortPresentation.options &&
      sortPresentation.selectedOption.label !== sortPresentation.options[0].label
    );
  }

  public isDefaultSortApplied(): boolean {
    const sortPresentation = this.sortAndFilterResult?.sortPresentation as LoadedSortPresentationType;

    return (
      sortPresentation &&
      sortPresentation.selectedOption &&
      sortPresentation.options &&
      sortPresentation.selectedOption.label === sortPresentation.options[0].label
    );
  }

  public selectFilter(filterItem: FlightsFilterItemType): void {
    filterItem.selected = !filterItem.selected;

    if (filterItem.selected) {
      this.increaseAppliedFilterCount();
    } else {
      this.decreaseAppliedFilterCount();
      this.removeFilterPill(filterItem.identifier);
    }
  }
  public applySortAndFilter(
    searchCriteriaStore: SearchCriteriaStore,
    history,
    location,
    context: ContextStore,
    analytics: AnalyticsStore
  ) {
    const { searchCriteria } = searchCriteriaStore;
    const { sortPresentation } = this.sortAndFilterResult || {};

    this.previousFilterSnapshot.isFilterAction = this.currentSelectedFilters.length > 0 ? true : false;

    searchCriteriaStore.setPreviousOriginalBookingId();
    searchCriteriaStore.syncWithSortAndFilterStore(this);
    searchCriteria.sortOption = sortPresentation?.selectedOption.sortType;
    const triggerSearchParams: TriggerFlightsSearchParams = {
      history,
      location,
      context,
      shouldForce: true,
      fetchFlexResults: false,
      fetchSearchForm: false
    };
    searchCriteriaStore.triggerSearch(triggerSearchParams);

    if (!this.actionInProgress) {
      this.actionInProgress = true;
      analyticsWrapper.resetSearchResultsAnalyticsPayLoad(analytics);
    }
  }

  public clearFilters(): void {
    this.loading = true;
    const { filterPresentation, revealAction, appliedFiltersSummary } = this.sortAndFilterResult || {};
    filterPresentation?.options?.forEach((filterOption: FlightsFilterLoadedOptionType): void => {
      filterOption.items &&
        filterOption.items.forEach((filterItem: FlightsFilterItemType): void => {
          filterItem.selected = false;
        });
    });
    if (revealAction) {
      revealAction.appliedFilterCount = 0;
    }
    if (appliedFiltersSummary) {
      appliedFiltersSummary.appliedFilters = [];
    }
  }

  public hasFiltersApplied(): boolean {
    return this.sortAndFilterResult?.revealAction.appliedFilterCount > 0;
  }

  public hasSortOrFilterApplied(): boolean {
    return this.hasFiltersApplied() || this.hasSortApplied();
  }

  public increaseAppliedFilterCount(): void {
    if (this.sortAndFilterResult) {
      this.sortAndFilterResult.revealAction.appliedFilterCount++;
    }
  }

  public decreaseAppliedFilterCount(): void {
    if (this.sortAndFilterResult) {
      this.sortAndFilterResult.revealAction.appliedFilterCount--;
    }
  }

  public removeFilterPill(identifier: string): void {
    const { appliedFiltersSummary } = this.sortAndFilterResult || {};
    if (identifier && appliedFiltersSummary) {
      appliedFiltersSummary.appliedFilters = appliedFiltersSummary.appliedFilters.filter(
        filter => filter.identifier !== identifier
      );
    }
  }

  public addSkeletonsOnLoading(): void {
    if (this.loading) {
      const { sortPresentation, appliedFiltersSummary, filterPresentation } = this.sortAndFilterResult || {};
      if (sortPresentation) {
        sortPresentation.__typename = 'FlightsSortLoadingPresentation';
      }
      if (!filterPresentation) {
        this.showFiltersSkeleton = true;
      }

      if (appliedFiltersSummary) {
        appliedFiltersSummary.appliedFilters = [];
      }
    }
  }

  public hasActionInProgressOrLoaded(): boolean {
    return this.actionInProgress || !this.loading;
  }

  public savePreviousFilterState(): void {
    const options = this.sortAndFilterResult.filterPresentation && this.sortAndFilterResult.filterPresentation.options;
    options?.forEach((filterOption: FlightsFilterLoadedOptionType): void => {
      filterOption.items?.forEach((filterItem: FlightsFilterItemType): void => {
        if (filterItem.selected) {
          this.currentSelectedFilters.push(filterItem);
        }
      });
    });

    this.previousFilterSnapshot.previousAppliedSummarySnapshot.appliedFilters = [
      ...this.sortAndFilterResult.appliedFiltersSummary.appliedFilters
    ];
  }

  public syncFilterInListWithFilterPill(filter: AppliedFiltersType): void {
    const filterPresentation = this.sortAndFilterResult.filterPresentation;

    const options = filterPresentation && filterPresentation.options;
    if (!options) {
      return;
    }

    options.forEach(filterOption => {
      if (
        filterOption.__typename === 'FlightsListDisplayFilterOption' ||
        filterOption.__typename === 'FlightsCardsDisplayFilterOption'
      ) {
        filterOption.items.forEach(filterItem => {
          if (filter.identifier === filterItem.identifier) {
            filterItem.selected = !filterItem.selected;
            this.currentSelectedFilters.push(filterItem);

            return;
          }
        });
      }
    });
  }

  public updatePreviousFilterSnapshot() {
    if (this.sortAndFilterResult.appliedFiltersSummary.appliedFilters.length) {
      this.previousFilterSnapshot.previousAppliedSummarySnapshot.appliedFilters = [
        ...this.sortAndFilterResult.appliedFiltersSummary.appliedFilters
      ];
    }
  }

  public revertToPreviousFilterState() {
    this.currentSelectedFilters.map(filterItem => {
      this.selectFilter(filterItem);
    });
    this.sortAndFilterResult.appliedFiltersSummary = this.previousFilterSnapshot.previousAppliedSummarySnapshot;

    this.previousFilterSnapshot.previousAppliedSummarySnapshot.appliedFilters = [];
    this.currentSelectedFilters = [];
  }

  public clearPreviousFilterState() {
    this.currentSelectedFilters = [];
    this.previousFilterSnapshot.previousAppliedSummarySnapshot.appliedFilters = [];
  }

  public setFilterActionFlag(isFilterAction: boolean) {
    this.previousFilterSnapshot.isFilterAction = isFilterAction;
  }

  @action
  public setSortFilterLoading(loading?: boolean) {
    this.loading = loading;
  }

  @action
  public setShowFiltersUSF(showFiltersUSF: boolean) {
    this.showFiltersUSF = showFiltersUSF;
  }
}

SortFilterStore.DEFAULTS = {
  loading: true,
  showFiltersUSF: true
};
